import { SVButton, SVLoader, SVTabs } from '@/components/core';
import DefaultLayout from '@/components/layouts/DefaultLayout';
import WireRecipientsStatusTable from '@/components/wireRecipients/list/WireRecipientsStatusTable';
import WireRequestStatusTable from '@/components/wireRecipients/list/WireRequestStatusTable';
import { ROUTES } from '@/constants/routes';
import { useGetCustomerDetails } from '@/hooks/requests/useCustomers';
import { Card, Stack, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

export default function WireRecipientsListPage() {
  const { customerId = '' } = useParams();
  const navigate = useNavigate();
  const { customerDetails, isCustomerDetailsPending } =
    useGetCustomerDetails(customerId);

  if (isCustomerDetailsPending) {
    return (
      <DefaultLayout header="Wire Recipients">
        <SVLoader />
      </DefaultLayout>
    );
  }

  if (!customerDetails) {
    return (
      <DefaultLayout header="Wire Recipients">
        <Card>
          <Typography>
            Something went wrong retreiving the customer. Our team has been
            notified.
          </Typography>
        </Card>
      </DefaultLayout>
    );
  }

  const { firstName, lastName, email } = customerDetails;

  return (
    <DefaultLayout
      header="Wire Recipients"
      subheader={
        <Typography>
          {firstName} {lastName} ({email})
        </Typography>
      }
      breadcrumb={[
        { label: 'Customers', href: ROUTES.CUSTOMERS },
        {
          label: `${firstName} ${lastName}`,
          href: `${ROUTES.CUSTOMERS}/${customerId}`,
        },
        { label: 'Wire Recipients', isActive: true },
      ]}
      buttons={[
        <SVButton
          key="add"
          aria-label="Add New Recipient"
          onClick={() =>
            navigate(
              `${ROUTES.CUSTOMERS}/${customerId}${ROUTES.WIRE_RECIPIENTS}/add`,
            )
          }>
          Add
        </SVButton>,
      ]}>
      <Stack spacing={2}>
        <Card>
          <Typography variant="h5" mb={2}>
            Recipient Status
          </Typography>
          <SVTabs
            headers={['Active', 'Deleted']}
            panels={[
              <WireRecipientsStatusTable
                key="activeTable"
                customerId={customerId}
                status="active"
              />,
              <WireRecipientsStatusTable
                key="deletedTable"
                customerId={customerId}
                status="deleted"
              />,
            ]}
          />
        </Card>
        <Card>
          <Typography variant="h5" mb={2}>
            Request Status
          </Typography>
          <SVTabs
            headers={[
              'Needs Approval',
              'Approved',
              'Declined',
              'Expired',
              'Canceled',
            ]}
            panels={[
              <WireRequestStatusTable
                key="needsApprovalTable"
                customerId={customerId}
                status="needs approval"
              />,
              <WireRequestStatusTable
                key="approvedTable"
                customerId={customerId}
                status="approved"
              />,
              <WireRequestStatusTable
                key="declinedTable"
                customerId={customerId}
                status="declined"
              />,
              <WireRequestStatusTable
                key="expiredTable"
                customerId={customerId}
                status="expired"
              />,
              <WireRequestStatusTable
                key="canceledTable"
                customerId={customerId}
                status="canceled"
              />,
            ]}
          />
        </Card>
      </Stack>
    </DefaultLayout>
  );
}
